// The vendor register's decisions (release 3.3, task V3 of the 7 Oct plan,
// section 3.2 "Vendors register").
//
// # WHY THIS EXISTS
//
// The register drew every row it had, kept its sort in the component, opened
// its tier-by-risk grid as a fold above the table, and its Excel export lived
// on a second vendor table (Portfolio). Now one list has two views, a page of
// 25 rows, and the page, the row size, the sort, the view and the filters are
// all in its address, so Back from a vendor lands on the same page of the same
// list. Each of those is a rule somebody relies on, and the console's tests
// run in node with no document, so the rules are plain functions here and
// views/Vendors.tsx draws what they return.
//
// # WHAT IT PROMISES
//
//  - The address is the register's whole state: readFilter and readPlace read
//    it, registerQuery writes it, and the defaults are left out so a plain
//    register stays #/vendors.
//  - A size, a page or a sort from a stale or hand-typed link is cleaned, never
//    drawn as "1-0 of 142" or a sort on a column the reader cannot see.
//  - The rows on screen and the rows in the Excel file are the same rows:
//    registerRows filters on the console, exportQuery asks the API for the
//    same thing (the test proves the two agree on fixtures).
//  - The empty register says why it is empty and offers the one way out:
//    first use offers Import and Add, a filter offers Clear filters.

import { cleanSize, DEFAULT_PAGE_SIZE } from '../components/kit/flow'
import type { Vendor, VendorQuery } from './api'

// ----------------------------------------------------------------- filter

export type RegisterFilter = {
  band: string[]
  tier: number | null
  unscored: boolean
  // These five are asked of the API rather than applied here, because they
  // are the filters every home card and overview number links with. Until the
  // register read them, "Exits in progress" and "Vendors you own" opened the
  // whole register, and the reader took the unfiltered list as the answer.
  status: string[]
  data: string[]
  owner: string
  crossBorder: boolean
  overdue: boolean
}

export const NO_FILTER: RegisterFilter = {
  band: [],
  tier: null,
  unscored: false,
  status: [],
  data: [],
  owner: '',
  crossBorder: false,
  overdue: false,
}

const csv = (p: URLSearchParams, k: string) =>
  (p.get(k) ?? '')
    .split(',')
    .map((x) => x.trim().toUpperCase())
    .filter(Boolean)

// The query part of the address (after the ?).
export function readFilter(query: string): RegisterFilter {
  const p = new URLSearchParams(query)
  const tier = Number(p.get('tier'))
  return {
    band: csv(p, 'band'),
    tier: p.get('tier') && Number.isInteger(tier) && tier >= 1 && tier <= 4 ? tier : null,
    unscored: p.get('unscored') === '1',
    status: csv(p, 'status'),
    data: [...csv(p, 'data'), ...csv(p, 'dataCategory')],
    owner: (p.get('owner') ?? '').trim(),
    crossBorder: p.get('crossBorder') === 'true',
    overdue: p.get('overdue') === 'true',
  }
}

export function filterParts(f: RegisterFilter): string[] {
  const parts: string[] = []
  if (f.tier != null) parts.push('tier=' + f.tier)
  if (f.band.length) parts.push('band=' + f.band.join(','))
  if (f.unscored) parts.push('unscored=1')
  if (f.status.length) parts.push('status=' + f.status.join(','))
  if (f.data.length) parts.push('data=' + f.data.join(','))
  if (f.owner) parts.push('owner=' + encodeURIComponent(f.owner))
  if (f.crossBorder) parts.push('crossBorder=true')
  if (f.overdue) parts.push('overdue=true')
  return parts
}

// What the register asks the API for (the rest it filters itself).
export function serverQuery(f: RegisterFilter): VendorQuery {
  const q: VendorQuery = {}
  if (f.status.length) q.status = f.status
  if (f.data.length) q.dataCategory = f.data
  if (f.owner) q.owner = f.owner
  if (f.crossBorder) q.crossBorder = true
  if (f.overdue) q.overdue = true
  return q
}

// Whether the reader narrowed the list at all, on the API or here.
export function isFiltered(f: RegisterFilter): boolean {
  return filterParts(f).length > 0
}

// ------------------------------------------------------------------ rows

// Offboarded and rejected vendors leave the register, unless somebody asked
// for a stage, in which case the API has already chosen the rows.
export const LIVE_STATUSES = ['DRAFT', 'PENDING_APPROVAL', 'ACTIVE', 'SUSPENDED', 'OFFBOARDING'] as const

// The bands asked for, "Not scored" as NONE. Within the one Risk band group
// the choices are alternatives: High and Not scored lists both (it listed
// neither: a band kept the High vendors out of "not scored" and the reverse).
export function bandsAsked(f: RegisterFilter): string[] {
  const out = [...f.band]
  if (f.unscored && !out.includes('NONE')) out.push('NONE')
  return out
}

// The header's search, matched the way the API matches q: the legal name, the
// name in use or the vendor code, ignoring case.
export function matchesSearch(v: Pick<Vendor, 'displayName' | 'legalName' | 'vendorCode'>, search: string): boolean {
  const s = search.trim().toLowerCase()
  if (!s) return true
  return [v.legalName, v.displayName ?? '', v.vendorCode].some((x) => x.toLowerCase().includes(s))
}

// The vendors on the register: the API's answer, less the ones that left
// (unless a stage was asked for), narrowed by tier, band and search.
export function liveVendors<T extends Pick<Vendor, 'status'>>(read: T[], f: RegisterFilter): T[] {
  return f.status.length ? read : read.filter((v) => (LIVE_STATUSES as readonly string[]).includes(v.status))
}

export function registerRows<T extends Pick<Vendor, 'status' | 'inherentTier' | 'currentBand' | 'displayName' | 'legalName' | 'vendorCode'>>(
  read: T[],
  f: RegisterFilter,
  search: string,
): T[] {
  const bands = bandsAsked(f)
  return liveVendors(read, f).filter(
    (v) =>
      (!bands.length || bands.includes(v.currentBand ?? 'NONE')) &&
      (f.tier == null || (v.inherentTier ?? 0) === f.tier) &&
      matchesSearch(v, search),
  )
}

// The Excel export asks for exactly the rows on screen: the stages are sent
// even when none was picked (the live ones), because the API's list includes
// the vendors that left and the register does not.
export function exportQuery(f: RegisterFilter, search: string): VendorQuery {
  const q: VendorQuery = { ...serverQuery(f), status: f.status.length ? f.status : [...LIVE_STATUSES] }
  if (f.tier != null) q.tier = [String(f.tier)]
  const bands = bandsAsked(f)
  if (bands.length) q.band = bands
  if (search.trim()) q.q = search.trim()
  return q
}

// ----------------------------------------------------------------- place

export type RegisterView = 'table' | 'grid'
export type RegisterSort = { key: string; dir: 'asc' | 'desc' }
export type RegisterPlace = { page: number; size: number; sort: RegisterSort; view: RegisterView }

// Riskiest first, as the register has always opened.
export const DEFAULT_SORT: RegisterSort = { key: 'band', dir: 'asc' }

// Riskiest first means the band's rank, never its code (review of V3, S4): by
// code Low sorted above Medium, and at 25 a page every Medium vendor sat pages
// behind every Low one. The order is the Excel export's (portfolioBandRank in
// apps/tprm-api/internal/api/portfolio_export.go, held by its Go test):
// Critical, Insufficient evidence, High, Medium, Low, then not scored.
const BAND_ORDER = new Map<string, number>([
  ['CRITICAL', 0],
  ['INSUFFICIENT_EVIDENCE', 1],
  ['HIGH', 2],
  ['MEDIUM', 3],
  ['LOW', 4],
])

export function bandOrder(band: string | null | undefined): number {
  return (band != null ? BAND_ORDER.get(band) : undefined) ?? 5
}

// The register's Risk column sorts on this: the rank, then the name, as the
// export orders its rows, so the screen and the file read in the same order.
export function bandSortValue(v: Pick<Vendor, 'currentBand' | 'displayName' | 'legalName'>): string {
  return bandOrder(v.currentBand) + ':' + (v.displayName?.trim() || v.legalName).toLowerCase()
}

// A new search is a new list: its first page (review of V3, S3). The address
// query at page 1, or null where it is already there.
export function pageOneQuery(query: string, sortKeys: readonly string[], added: string[] = []): string | null {
  const p = readPlace(query, sortKeys)
  return p.page > 1 ? registerQuery(readFilter(query), firstPage(p), added) : null
}

// "Table" unless the address says grid; anything else is the table.
export function viewOf(raw: string | null | undefined): RegisterView {
  return raw === 'grid' ? 'grid' : 'table'
}

// The page, the size, the sort and the view in the address. `sortKeys` are the
// columns this reader has: a sort on a column they are not shown (the
// questionnaire, without assessment.read) falls back to the default.
export function readPlace(query: string, sortKeys: readonly string[]): RegisterPlace {
  const p = new URLSearchParams(query)
  const n = Number(p.get('page'))
  const key = p.get('sort') ?? ''
  return {
    page: Number.isInteger(n) && n >= 1 ? n : 1,
    size: cleanSize(p.get('size')),
    sort: sortKeys.includes(key) ? { key, dir: p.get('dir') === 'desc' ? 'desc' : 'asc' } : DEFAULT_SORT,
    view: viewOf(p.get('view')),
  }
}

export function placeParts(p: RegisterPlace): string[] {
  const out: string[] = []
  if (p.view === 'grid') out.push('view=grid')
  if (p.sort.key !== DEFAULT_SORT.key || p.sort.dir !== DEFAULT_SORT.dir) out.push('sort=' + p.sort.key, 'dir=' + p.sort.dir)
  if (p.page > 1) out.push('page=' + p.page)
  const size = cleanSize(p.size)
  if (size !== DEFAULT_PAGE_SIZE) out.push('size=' + size)
  return out
}

// The whole address after #/vendors?: the filters, the place, and the vendors
// just added (the confirmation stays until it is closed, so paging keeps it).
export function registerQuery(f: RegisterFilter, p: RegisterPlace, added: string[] = []): string {
  return [...filterParts(f), ...placeParts(p), ...(added.length ? ['added=' + added.join(',')] : [])].join('&')
}

// A new filter, sort or size starts at the first page: page 4 of the old list
// is not a place in the new one.
export function firstPage(p: RegisterPlace): RegisterPlace {
  return { ...p, page: 1 }
}

// --------------------------------------------------------------- empty

export type EmptyWay = 'import' | 'add' | 'clear'
export type EmptyState = { title: string; detail: string; actions: EmptyWay[] }

// Why the list is empty, and the one way out (plan 3.2; the Carbon and Nielsen
// Norman Group empty-state rules): "Import vendors" or "Add a vendor" only on
// first use, "Clear filters" when a filter emptied it, and nothing to press
// when the header's search did (the search box is where it is cleared).
export function emptyState(o: { read: number; filtered: boolean; searched: boolean; canImport: boolean; canAdd: boolean }): EmptyState {
  if (o.filtered)
    return {
      title: 'Nothing matches.',
      detail: o.searched ? 'No vendor matches these filters and the search.' : 'No vendor matches these filters.',
      actions: ['clear'],
    }
  if (o.searched) return { title: 'Nothing matches.', detail: 'No vendor name or code matches the search.', actions: [] }
  if (o.read === 0) {
    const actions: EmptyWay[] = [...(o.canImport ? (['import'] as const) : []), ...(o.canAdd ? (['add'] as const) : [])]
    return {
      title: 'No vendors yet.',
      detail: actions.length ? 'Import your vendor list from a spreadsheet, or add one vendor.' : 'The register is empty.',
      actions,
    }
  }
  return {
    title: 'No vendors on the register.',
    detail: 'Every vendor here was offboarded or rejected. Pick those stages under Filters to see them.',
    actions: [],
  }
}

// ------------------------------------------------------------------ grid

export const GRID_BANDS = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL', 'INSUFFICIENT_EVIDENCE', 'NONE'] as const

// A grid number opens the table narrowed to that cell. The other filters stay,
// because they chose the vendors the number counted; the tier and the band
// are replaced, and the list starts at its first page.
export function gridCellQuery(f: RegisterFilter, p: RegisterPlace, tier: number, band: string): string {
  return registerQuery({ ...f, tier, band: [band], unscored: false }, { ...firstPage(p), view: 'table' })
}

// Which vendors a grid cell counts.
export function inCell(v: Pick<Vendor, 'inherentTier' | 'currentBand'>, tier: number, band: string): boolean {
  return (v.inherentTier ?? 0) === tier && (v.currentBand ?? 'NONE') === band
}
